import App from "App";
import useFeatureFlag from "hooks/useFeatureFlag";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router-dom";
import Api from "./pages/Api";
import ApiCredentials from "./pages/Api/Credentials/ApiCredentials";
import OauthApplicationForm from "./pages/Api/Credentials/OauthApplicationForm";
import WebhookForm from "./pages/Api/Webhooks/WebhookForm";
import Webhooks from "./pages/Api/Webhooks/Webhooks";
import Assets from "./pages/Assets";
import AssetCreate from "./pages/Assets/AssetCreate";
import AssetsDetails from "./pages/Assets/AssetsDetails";
import AssetHistory from "./pages/Assets/AssetsDetails/AssetHistory";
import CompanySettings from "./pages/CompanySettings";
import { Authentication } from "./pages/CompanySettings/Authentication/Authentication";
import { ConfigureProvider } from "./pages/CompanySettings/Authentication/ConfigureProvider";
import { SelectProvider } from "./pages/CompanySettings/Authentication/SelectProvider";
import Locations from "./pages/CompanySettings/Locations";
import LocationEdit from "./pages/CompanySettings/Locations/Edit";
import LocationsList from "./pages/CompanySettings/Locations/List";
import LocationNew from "./pages/CompanySettings/Locations/New";
import Overview from "./pages/CompanySettings/Overview";
import PaymentInfo from "./pages/CompanySettings/PaymentInfo";
import ACHInformation from "./pages/CompanySettings/PaymentInfo/ACHInformation";
import Users from "./pages/CompanySettings/Users";
import EditUser from "./pages/CompanySettings/Users/Edit";
import NewUser from "./pages/CompanySettings/Users/New";
import UserManagement from "./pages/CompanySettings/Users/UserManagement";
import DonationsAndEarnings from "./pages/DonationsAndEarnings";
import DonationDetails from "./pages/DonationsAndEarnings/DonationRequestDetails";
import Donations from "./pages/DonationsAndEarnings/Donations";
import Earnings from "./pages/DonationsAndEarnings/Earnings";
import Home from "./pages/Home";
import Login from "./pages/Login";
import CreateResetPassword from "./pages/Login/createResetPassword";
import FindOrganization from "./pages/Login/findOrganization";
import NoConnectionToBackend from "./pages/Login/noConnectionToBackend";
import ResetPassword from "./pages/Login/resetPassword";
import Pickups from "./pages/Pickups";
import SchedulePickup from "./pages/Pickups/SchedulePickup/SchedulePickup";
import Summary from "./pages/Pickups/SchedulePickup/Summary";
import Profile from "./pages/Profile";
import Documents from "./pages/ReportingAndDocuments/Documents";
import StandardReports from "./pages/ReportingAndDocuments/StandardReports";
import ReportingAndDocuments from "./pages/ReportingAndDocuments/index";
import NewAssetRetrieval from "./pages/ShippingAndRetrieval/NewAssetRetrieval";
import BulkOrder from "./pages/ShippingAndRetrieval/NewAssetRetrieval/BulkOrder";
import SendBoxes from "./pages/ShippingAndRetrieval/SendBoxes";
import SendBoxes2 from "./pages/ShippingAndRetrieval/SendBoxes/index2";
import SignUp from "./pages/SignUp";
import Support from "./pages/Support";
import SupportArticleDetail from "./pages/Support/SupportArticleDetail";
import UserAcceptInvitation from "./pages/UserAcceptInvitation";
import UserSetup from "./pages/UserSetup";
import AddLocations from "./pages/UserSetup/AddLocations";
import CompanyInfo from "./pages/UserSetup/CompanyInfo";
import CreateLocation from "./pages/UserSetup/CreateLocation";
import { default as EarningsWithdrawalPage, loader as earningsWithdrawalLoader } from "./pages/EarningsWithdrawalPage";

const RetrievalsPage = () => {
  const { enabled: assetRetrievalDevicesEnabled } = useFeatureFlag("asset_retrieval_devices");
  return assetRetrievalDevicesEnabled ? <SendBoxes2 /> : <SendBoxes />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<App />}>
      <Route index element={<Home />} />
      <Route path="/login" element={<Login />} />

      <Route path="/create-reset-password" element={<CreateResetPassword />} />

      <Route path="/find-organization" element={<FindOrganization />} />

      <Route path="/no-backend-connection" element={<NoConnectionToBackend />} />

      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/signup" element={<SignUp />} />

      <Route path="/accept-invitation" element={<UserAcceptInvitation />} />

      <Route path="user-setup" element={<UserSetup />}>
        <Route index element={<Navigate to="/signup" replace />} />
        <Route path="company-info" element={<CompanyInfo />} />
        <Route path="add-locations" element={<AddLocations />} />
        <Route path="add-locations/create" element={<CreateLocation />} />
      </Route>

      <Route path="/profile" element={<Profile />} />

      <Route path="/support" element={<Support />} />

      <Route path="/support/article/:articleId" element={<SupportArticleDetail />} />

      <Route path="/pickups" element={<Pickups />} />
      <Route path="/pickups/:pickupUuid" element={<SchedulePickup />} />

      <Route path="/pickups/:pickupUuid/summary" element={<Summary />} />

      <Route path="/assets" element={<Assets />} />
      <Route path="/assets/new" element={<AssetCreate />} />
      <Route path="/assets/:assetUuid" element={<AssetsDetails />}>
        <Route index element={<Navigate to="/history" replace />} />
        {/* <Route path="details" element={<AssetDetails />} /> */}
        <Route path="history" element={<AssetHistory />} />
        {/* <Route path="checkout" element={<AssetCheckout />} /> */}
      </Route>

      <Route path="/retrievals/send-boxes" element={<RetrievalsPage />}></Route>

      <Route path="/retrievals/send-boxes/new" element={<NewAssetRetrieval />}></Route>

      <Route path="/retrievals/send-boxes/bulk-new" element={<BulkOrder />}></Route>

      <Route path="/donations-and-earnings" element={<DonationsAndEarnings />}>
        <Route index element={<Navigate to="/donations-and-earnings/donations" replace />} />
        <Route path="donations" element={<Donations />} />
        <Route path="donations/:donationRequestUuid" element={<DonationDetails />} />
        <Route path="earnings" element={<Earnings />}>
          <Route path="withdraw" element={<EarningsWithdrawalPage />} loader={earningsWithdrawalLoader} />
        </Route>
      </Route>

      <Route path="/reporting-and-documents" element={<ReportingAndDocuments />}>
        <Route index element={<Navigate to="/reporting-and-documents/reporting/reports" replace />} />
        <Route path="reporting" element={<Navigate to="/reporting-and-documents/reporting/reports" replace />} />
        <Route path="reporting/reports" element={<StandardReports />} />
        {/* <Route path="reporting/custom-reports" element={<CustomReports />} /> */}
        {/* <Route path="reporting/custom-reports/create-report" element={<CreateReport />} /> */}
        <Route path="documents" element={<Documents />} />
      </Route>

      <Route path="/api" element={<Api />}>
        <Route index element={<Navigate to="/api/credentials" replace />} />
        <Route path="credentials" element={<ApiCredentials />} />
        <Route path="credentials/new" element={<OauthApplicationForm />} />
        <Route path="webhooks" element={<Webhooks />} />
        <Route path="webhooks/new" element={<WebhookForm />} />
      </Route>

      <Route path="/company-settings" element={<CompanySettings />}>
        <Route index element={<Navigate to="/company-settings/overview" replace />} />
        <Route path="overview" element={<Overview />} />
        <Route path="locations" element={<Locations />}>
          <Route index element={<LocationsList />} />
          <Route path="new" element={<LocationNew />} />
          <Route path=":uuid/edit" element={<LocationEdit />} />
        </Route>
        <Route index element={<Navigate to="/company-settings/overview" replace />} />

        <Route path="users" element={<Users />}>
          <Route index element={<UserManagement />} />
          <Route path="new" element={<NewUser />} />
          <Route path=":uuid/edit" element={<EditUser />} />
        </Route>
        <Route path="payment-info" element={<PaymentInfo />} />
        <Route path="payment-info/ach-information" element={<ACHInformation />} />
        <Route path="authentication" element={<Authentication />} />
        <Route path="authentication/provider" element={<SelectProvider />} />
        <Route path="authentication/provider/:provider" element={<ConfigureProvider />} />
        {/* <Route path="integrations" element={<Integrations />} /> */}
      </Route>
    </Route>
  )
);

export default router;
